import React, { useEffect, useState } from 'react';
import { UserPlus, Trash2, Mail, Building2, AlertTriangle, Search, X, Copy, Check } from 'lucide-react';
import { ResponsabileService, SedeService } from '../services/apiService';
import { User, Sede } from '../types';
import { useToast } from "../components/ToastContext";

export const ResponsabiliView: React.FC = () => {
    const { addToast } = useToast();

    // Dati
    const [responsabili, setResponsabili] = useState<User[]>([]);
    const [sedi, setSedi] = useState<Sede[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modale Invito
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [inviteData, setInviteData] = useState({ name: '', surname: '', email: '' });
    const [generatedLink, setGeneratedLink] = useState<string | null>(null);
    const [isInviting, setIsInviting] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [respRes, sediRes] = await Promise.all([
                ResponsabileService.getAll(),
                SedeService.getAll()
            ]);
            setResponsabili(respRes.data);
            setSedi(sediRes.data);
        } catch (error) {
            addToast("Errore caricamento dati", "error");
        } finally {
            setLoading(false);
        }
    };

    // Helper: Trova la sede assegnata al responsabile
    const getAssignedSede = (user: User) => {
        // Prima il riferimento diretto restituito dal backend
        if (user.sedeId) {
            return sedi.find(s => s.id === user.sedeId);
        }
        // Altrimenti cerca la sede che punta a questo utente
        return sedi.find(s => s.responsabileId === user.id);
    };

    const handleDelete = async (user: User) => {
        const sedeAssegnata = getAssignedSede(user);

        if (sedeAssegnata) {
            addToast(`Impossibile eliminare: È responsabile della sede "${sedeAssegnata.name}". Cambia prima il responsabile della sede.`, 'error');
            return;
        }

        if (window.confirm(`Eliminare definitivamente ${user.name} ${user.surname}?`)) {
            try {
                await ResponsabileService.delete(user.id);
                addToast("Responsabile eliminato", "success");
                setResponsabili(prev => prev.filter(u => u.id !== user.id));
            } catch (e) {
                addToast("Errore durante l'eliminazione", "error");
            }
        }
    };

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsInviting(true);
        try {
            const response = await ResponsabileService.invite(inviteData);
            if (response.success) {
                setGeneratedLink(response.data); // Il backend restituisce il link in 'data'
                loadData(); // Ricarica la lista nel caso l'utente sia già stato creato
            } else {
                addToast(response.message || "Errore creazione invito", "error");
            }
        } catch (error: any) {
            const msg = error.response?.data?.message || "Errore del server";
            addToast(msg, "error");
        } finally {
            setIsInviting(false);
        }
    };

    const closeAndResetModal = () => {
        setIsModalOpen(false);
        setGeneratedLink(null);
        setInviteData({ name: '', surname: '', email: '' });
    };

    const filteredList = responsabili.filter(r =>
        r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestione Responsabili</h1>
                    <p className="text-sm text-gray-500">Elenco dei responsabili di sede.</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
                >
                    <UserPlus size={18}/> Invita Responsabile
                </button>
            </div>

            {/* Ricerca */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                <Search className="text-gray-400" size={20}/>
                <input
                    type="text"
                    placeholder="Cerca per nome, cognome o email..."
                    className="flex-1 outline-none text-sm"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Griglia */}
            {loading ? <div className="text-center p-8">Caricamento...</div> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {filteredList.map(resp => {
                        const sede = getAssignedSede(resp);
                        const isAssigned = !!sede;

                        return (
                            <div key={resp.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-all relative group">
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold uppercase ${isAssigned ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                                        {resp.name.charAt(0)}{resp.surname ? resp.surname.charAt(0) : ''}
                                    </div>
                                    <button
                                        onClick={() => handleDelete(resp)}
                                        className={`p-2 rounded-lg transition-colors ${isAssigned ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                                        title={isAssigned ? "Impossibile eliminare: Utente assegnato a una sede" : "Elimina utente"}
                                    >
                                        <Trash2 size={18}/>
                                    </button>
                                </div>

                                <h3 className="font-bold text-gray-900 text-lg mb-1">
                                    {resp.name} {resp.surname}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 truncate">
                                    <Mail size={14} className="flex-shrink-0"/> {resp.email}
                                </div>

                                <div className="pt-4 border-t border-gray-100">
                                    <div className="text-xs font-bold text-gray-400 uppercase mb-2">Sede Assegnata</div>
                                    {isAssigned ? (
                                        <div className="flex items-center gap-2 text-sm font-medium text-purple-700 bg-purple-50 p-2 rounded-lg border border-purple-100">
                                            <Building2 size={16}/>
                                            <span className="truncate">{sede?.name}</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-2 rounded-lg border border-gray-200">
                                            <AlertTriangle size={16} className="text-yellow-500"/>
                                            Non assegnato
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modale invito */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
                            <h2 className="text-lg font-bold text-gray-900">Nuovo Responsabile</h2>
                            <button onClick={closeAndResetModal} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        {!generatedLink ? (
                            <form onSubmit={handleInvite} className="p-6 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                                        <input
                                            type="text"
                                            required
                                            value={inviteData.name}
                                            onChange={e => setInviteData({...inviteData, name: e.target.value})}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Mario"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                                        <input
                                            type="text"
                                            required
                                            value={inviteData.surname}
                                            onChange={e => setInviteData({...inviteData, surname: e.target.value})}
                                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="Rossi"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={inviteData.email}
                                        onChange={e => setInviteData({...inviteData, email: e.target.value})}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="mario@azienda.it"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isInviting}
                                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex justify-center items-center gap-2"
                                >
                                    {isInviting ? 'Generazione...' : 'Crea e Genera Invito'}
                                </button>
                            </form>
                        ) : (
                            <div className="p-6 text-center">
                                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Check size={24} />
                                </div>
                                <h3 className="font-bold text-gray-900 mb-2">Invito Generato!</h3>
                                <p className="text-sm text-gray-500 mb-4">Copia il link qui sotto e invialo al nuovo responsabile.</p>

                                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-blue-600 break-all font-mono mb-4 select-all">
                                    {generatedLink}
                                </div>

                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(generatedLink);
                                        addToast("Link copiato!", "success");
                                    }}
                                    className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium flex justify-center items-center gap-2 mb-2"
                                >
                                    <Copy size={16} /> Copia Link
                                </button>
                                <button
                                    onClick={closeAndResetModal}
                                    className="w-full py-2 text-gray-500 hover:text-gray-700"
                                >
                                    Chiudi
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResponsabiliView;